import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';

import { PackagesSelectionModal } from '@/lazy-load-components';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { calculateUrl } from '@/helpers/state-helper';

import Provider from '@/interfaces/provider';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import { useRouter } from 'vue-router/composables';
import ResetButton from '@/components/buttons/ResetButton.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

type FilterBarPopularProviderProps = {
	addButtonVisible?: boolean;
	showAddButtonFirst?: boolean;
	collectionType: CollectionType;
	currentFilters: FilterCollection;
	providersClickable?: boolean;
	userProviders?: Provider[];
	updateRouteState?: boolean; // when toggling providers
	trackEvent?: boolean; // when toggling providers
	userSelectedPackages?: string[]; // used for sports provider until we have a better flagging mechanism in place
};


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterBarPopularProvider',
  props: {
    addButtonVisible: { type: Boolean, default: true },
    showAddButtonFirst: { type: Boolean },
    collectionType: null,
    currentFilters: null,
    providersClickable: { type: Boolean, default: true },
    userProviders: { default: () => [] },
    updateRouteState: { type: Boolean, default: true },
    trackEvent: { type: Boolean, default: true },
    userSelectedPackages: { default: () => [] }
  },
  emits: ["toggle"],
  setup(__props: any, { emit: emits }: { emit: ({
	(
		e: 'toggle',
		toggleData: {
			filterName: string;
			value: string;
			selectedState: 0 | 1;
			updateRouteState: boolean;
			userSelectedPackages: string[];
			trackEvent: boolean;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { addButtonVisible: boolean, showAddButtonFirst?: boolean, collectionType: CollectionType, currentFilters: FilterCollection, providersClickable: boolean, userProviders: Provider[], updateRouteState: boolean, trackEvent: boolean, userSelectedPackages: string[] };





const filterStore = useFilterStore();
const constantStore = useConstantStore();
const userStore = useUserStore();
const languageStore = useLanguageStore();

const router = useRouter();
const isModalOpening = ref(false);

const selectedProvider = computed(() => {
	return props.userProviders.find(el => el.shortName === props.currentFilters.providers?.[0]);
});

const IMG_PROVIDERS_BAR_LOAD_COUNT = computed(() => {
	return global.IMG_PROVIDERS_BAR_LOAD_COUNT;
});

const plusIcon = computed(() => {
	return `/${ASSETS_DIR}/img/plus_icon.png`;
});

const plusIconIsVisible = computed(() => {
	// causing issue with SSR > client hydration

	if (props.addButtonVisible) {
		return userStore.settings.providers?.length > 0;
	}
	return props.addButtonVisible;
});

const providerUrls = ref<Record<string, string>>({});

const _providersById = ref(constantStore.providersById);

// SSR perf: only call providersById once instead of every v-for loop.
watch(
	() => props.userProviders,
	() => {
		_providersById.value = constantStore.providersById;
	},
	{ immediate: true }
);

watch(
	() => languageStore.language,
	() => {
		// every call to a vuex getter seems to cost performance, that's why we only call it once before the loop
		const _providersByShortName = constantStore.providersByShortName;
		const _genresByShortName = constantStore.genresByShortName;
		// cached getters don't work in SSR, that's why we optimize it by putting it into data
		// disable subgenre parametric urls for better interlinking
		const { subgenres, ...filters } = props.currentFilters;
		void subgenres;

		providerUrls.value = props.userProviders.reduce((root, provider: Provider) => {
			const url = calculateUrl(
				router,
				undefined,
				props.collectionType,
				{ ...filters, providers: [provider.shortName] },
				_providersByShortName,
				_genresByShortName
			);

			if (url) root[provider.shortName] = url;

			return root;
		}, {} as Record<string, string>);
	},
	{ immediate: true }
);

async function providerClicked(providerName: string) {
	if (props.providersClickable) {
		const isActive: 1 | 0 = isFiltersContainsProvider(providerName) ? 0 : 1;
		const toggleData = {
			filterName: 'providers',
			value: providerName,
			selectedState: isActive,
			updateRouteState: props.updateRouteState,
			userSelectedPackages: props.userSelectedPackages,
			trackEvent: props.trackEvent,
		};

		const newRoute = await filterStore.toggle(toggleData as any);
		emits('toggle', toggleData);

		if (newRoute) router.push(newRoute);
	}
}
function isFiltersContainsProvider(providerName: string): boolean {
	return props.currentFilters.providers.includes(providerName);
}
function isProviderActive(providerName: string): boolean {
	return props.currentFilters.providers.length === 0 || isFiltersContainsProvider(providerName);
}

function onWillDismiss() {
	isModalOpening.value = false;
}

async function openProviderSelection() {
	isModalOpening.value = true;

	await ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS, onWillDismiss },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
	isModalOpening.value = false;
}

return { __sfc: true,props, emits, filterStore, constantStore, userStore, languageStore, router, isModalOpening, selectedProvider, IMG_PROVIDERS_BAR_LOAD_COUNT, plusIcon, plusIconIsVisible, providerUrls, _providersById, providerClicked, isFiltersContainsProvider, isProviderActive, onWillDismiss, openProviderSelection, ResetButton, ProviderIcon, HiddenHorizontalScrollbar }
}

})